import * as React from "react";
import { omit } from "ramda";
import type { IColumn, IObjectWithKey } from "@fluentui/react";
import { SelectionMode, Stack } from "@fluentui/react";
import type { UUID } from "../../utils/uuid";
import { createUuid } from "../../utils/uuid";
import { LayoutList } from "../layouts/layout-list";
import type { ElofleetListProps } from "../atoms/elofleet-list";
import { ElofleetList } from "../atoms/elofleet-list";
import data from "../../utils/mock/eloshield-data.json";
import type { Zone } from "../atoms/list-zone-event-item-cell";
import { ListZoneEventItemCell } from "../atoms/list-zone-event-item-cell";
import { store } from "../../store";

export interface ListZoneEventsProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: string;
}

interface ListZoneEventItem extends IObjectWithKey {
    readonly key: number;
    readonly data: object;
}

export class ListZoneEvents extends React.Component<ListZoneEventsProps> {
    private listStateId = createUuid();

    private get items(): (ListZoneEventItem | undefined)[] {
        return data["zoning"].map((zoneItem) => {
            if (!zoneItem) {
                return;
            }

            return {
                key: zoneItem.id,
                data: zoneItem,
            };
        });
    }

    private get columns(): IColumn[] {
        const columns = [
            {
                fieldName: "event-time",
                name: store.i18n.t("listEvents.column.time.name"),
                key: "event-time",
                minWidth: 150,
                maxWidth: 200,
            },
            {
                fieldName: "stationary-id",
                name: store.i18n.t("listEvents.column.stationary.unit.id"),
                key: "stationary-id",
                minWidth: 100,
                maxWidth: 200,
            },
            {
                fieldName: "zone-name",
                name: store.i18n.t("listEvents.column.zone.name"),
                key: "zone-name",
                minWidth: 150,
                maxWidth: 200,
            },
            {
                fieldName: "zone-trigger",
                name: store.i18n.t("listEvents.column.zone.trigger"),
                key: "zone-trigger",
                minWidth: 100,
                maxWidth: 200,
            },
            {
                fieldName: "detected-eloshield-id",
                name: store.i18n.t("listEvents.column.detected.eloshield.id"),
                key: "detected-eloshield-id",
                minWidth: 150,
                maxWidth: 200,
            },
            {
                fieldName: "detected-truck-id",
                name: store.i18n.t("listEvents.column.detected.truck.id"),
                key: "detected-truck-id",
                minWidth: 150,
                maxWidth: 200,
            },

            {
                fieldName: "oem",
                name: store.i18n.t("listEvents.column.oem"),
                key: "oem",
                minWidth: 100,
                maxWidth: 200,
            },
            {
                fieldName: "oem-type",
                name: store.i18n.t("listEvents.column.oem.type"),
                key: "oem-type",
                minWidth: 100,
                maxWidth: 200,
            },
            {
                fieldName: "duration",
                name: store.i18n.t("listEvents.column.duration"),
                key: "duration",
                minWidth: 150,
                maxWidth: 200,
            },
        ];

        return columns;
    }

    private renderItem(truckEventId: number, column: IColumn, item: Zone): JSX.Element {
        return <ListZoneEventItemCell truckEventId={truckEventId} column={column} item={item} />;
    }

    public render(): JSX.Element {
        if (!this.listStateId) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);

        return (
            <Stack>
                <Stack>
                    <LayoutList commandBar={this.props.commandBar}>
                        <ElofleetList
                            {...props}
                            onColumnHeaderClick={() => {}}
                            // Disable right-click to stay consistent with other lists
                            onItemContextMenu={() => {}}
                            selectionMode={SelectionMode.none}
                            onRenderItemColumn={(item: Zone, _index, column) =>
                                this.renderItem(item.key, column!, item)
                            }
                            columns={this.columns}
                            items={this.items}
                        />
                    </LayoutList>
                </Stack>
            </Stack>
        );
    }
}
